import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { handleAxiosError } from 'ui';
import { isValidationResponseErrorDataObject } from 'common/services';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { useLoginMutation, useLoginToNodeMutation } from 'features/users/queries';
import { useAuthenticateMutation, useSignUpMutation } from '../queries';
import { SignUpContent, type SignUpFormValues } from './sign-up-content';

type P = {
  changeRequestId?: string;
  communityId?: string;
  invitationToken?: string;
  onLoginError: () => void;
  onNavigateToCheckCode: ({ tokenToGetCode, tokenToLogin }: { tokenToGetCode?: string; tokenToLogin?: string }) => void;
  onSuccessLogin: () => Promise<void>;
  publicCommunityId?: string;
};

const SignUp = ({
  changeRequestId,
  communityId,
  invitationToken,
  onLoginError,
  onNavigateToCheckCode,
  onSuccessLogin,
  publicCommunityId,
}: P) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { isPending: isLoadingSignUp, mutateAsync: signUp } = useSignUpMutation();
  const { mutateAsync: loginAsync } = useLoginMutation();
  const { mutateAsync: nodeLoginAsync } = useLoginToNodeMutation();

  const { isPending: isLoading, mutate: authenticate } = useAuthenticateMutation();

  const handleSubmit = async (values: SignUpFormValues) => {
    const recaptchaToken = await executeRecaptcha?.('sign_up');

    await signUp(
      {
        email: values.email,
        firstName: values.firstName,
        invitationToken,
        lastName: values.lastName,
        organizationId: values.organization?.value,
        password: values.password,
        publicCommunityId,
        recaptchaToken,
      },
      {
        onError(error) {
          const errorData = error.response?.data;
          const customMessage = isValidationResponseErrorDataObject(errorData)
            ? Object.entries(errorData.errors)
                .map(([key, value]) => value.map((item: string) => `${key} ${item}`).join(', '))
                .join(', ')
            : undefined;
          const capitalizedCustomMessage = customMessage
            ? customMessage.charAt(0).toUpperCase() + customMessage.slice(1)
            : undefined;

          handleAxiosError(error, capitalizedCustomMessage);
        },
      },
    );

    authenticate(
      {
        async nodeLogin() {
          return nodeLoginAsync({
            email: values.email,
            password: values.password,
            strategy: 'password',
          });
        },
        async railsLogin() {
          return loginAsync(values);
        },
      },
      {
        onError(error) {
          handleAxiosError(error);
          onLoginError();
        },
        async onSuccess() {
          await onSuccessLogin();
        },
      },
    );
  };

  return (
    <SignUpContent
      changeRequestId={changeRequestId}
      communityId={communityId}
      invitationToken={invitationToken}
      loading={isLoadingSignUp || isLoading}
      onNavigateToCheckCode={onNavigateToCheckCode}
      onSubmit={handleSubmit}
      onSuccessLogin={onSuccessLogin}
    />
  );
};

export default SignUp;
